import type {LocationEcoregionData} from "../types";
import {BIRDING_HABITAT_PROFILE_VERSION, type HabitatFamilyLabel} from "./constants";
import {
    getBiomeTerms,
    getCoordinateTerms,
    getEcoregionTerms,
    getElevationBand,
    getHotspotNameTerms,
    getLandCoverFamily,
    getPreferredHabitatMix,
    getPreferredHabitatPrimary,
    getPreferredHabitatSecondary,
    getPrimaryHabitatLabel,
    getProfileConfidence,
    getTerrainSummary,
    getWaterContext,
    makeReasonList,
    normalizeHabitatMixLabel,
    normalizeNumber,
    normalizeSearchText,
    normalizeText,
    slugifyHabitatLabel,
    toTitleCase,
    uniqueStrings
} from "./helpers";
import {
    getBirdingHabitatMatchExplanation,
    getMatchedCanonicalHabitatRule,
    getMatchedDerivedSubtypeHabitatRule,
    getMatchedHabitatRule
} from "./rules";
import type {BirdingHabitatProfile, HabitatClassificationContext, HabitatSourceBasis} from "./types";

type HabitatClassificationBuildOptions = {
    hotspotName?: string;
    latitude?: number;
    longitude?: number;
};

const getSourceFields = (ecoData: LocationEcoregionData) => {
    const sourceFields = new Set<string>();
    const add = (field: string, value: unknown) => {
        if (Array.isArray(value)) {
            if (value.length) sourceFields.add(field);
            return;
        }

        if (typeof value === 'string') {
            if (value.trim()) sourceFields.add(field);
            return;
        }

        if (typeof value === 'number') {
            if (Number.isFinite(value)) sourceFields.add(field);
            return;
        }

        if (value !== null && value !== undefined) {
            sourceFields.add(field);
        }
    };

    add('habitat_primary', ecoData.habitat_primary);
    add('habitat_secondary', ecoData.habitat_secondary);
    add('habitat_mix', ecoData.habitat_mix);
    add('habitat_local_primary', ecoData.habitat_local_primary);
    add('habitat_local_secondary', ecoData.habitat_local_secondary);
    add('habitat_local_mix', ecoData.habitat_local_mix);
    add('habitat_local_sample_count', ecoData.habitat_local_sample_count);
    add('habitat_local_h3_cells', ecoData.habitat_local_h3_cells);
    add('habitat_local_method', ecoData.habitat_local_method);
    add('habitat_local_radius_m', ecoData.habitat_local_radius_m);
    add('habitat_group', ecoData.habitat_group);
    add('habitat_dominant_worldcover_label', ecoData.habitat_dominant_worldcover_label);
    add('worldcover_label', ecoData.worldcover_label);
    add('biomeName', ecoData.biomeName);
    add('ecoregionName', ecoData.ecoregionName);
    add('realm', ecoData.realm);
    add('water_distance_m', ecoData.water_distance_m);
    add('water_proximity_label', ecoData.water_proximity_label);
    add('terrain_source', ecoData.terrain_source);
    add('terrain_source_version', ecoData.terrain_source_version);
    add('terrain_elevation_m', ecoData.terrain_elevation_m);
    add('terrain_elevation_band', ecoData.terrain_elevation_band);
    add('coastline_source', ecoData.coastline_source);
    add('coastline_source_layer', ecoData.coastline_source_layer);
    add('coastline_source_scale', ecoData.coastline_source_scale);
    add('coastline_source_version', ecoData.coastline_source_version);
    add('distance_to_coast_m', ecoData.distance_to_coast_m);
    add('coastal_context', ecoData.coastal_context);
    add('is_coastal', ecoData.is_coastal);
    add('is_near_coastal', ecoData.is_near_coastal);
    add('coastline_confidence', ecoData.coastline_confidence);
    add('marine_context', ecoData.marine_context);

    return Array.from(sourceFields);
};

const getHabitatMixLabels = (habitatMix?: LocationEcoregionData['habitat_mix']) => {
    if (!habitatMix || typeof habitatMix !== 'object' || Array.isArray(habitatMix)) return [];

    return Object.entries(habitatMix)
        .map(([key, value]) => ({
            label: normalizeHabitatMixLabel(key),
            percent: normalizeNumber(value) ?? 0
        }))
        .filter(item => item.label && item.percent > 0)
        .sort((a, b) => b.percent - a.percent)
        .map(item => item.label);
};

const buildContext = (ecoData: LocationEcoregionData, options?: HabitatClassificationBuildOptions): HabitatClassificationContext => {
    const hotspotName = normalizeText(options?.hotspotName);
    const hotspotNameTerms = getHotspotNameTerms(hotspotName);
    const latitude = normalizeNumber(options?.latitude);
    const longitude = normalizeNumber(options?.longitude);
    const coordinateTerms = getCoordinateTerms(latitude, longitude);
    const primaryHabitat = getPreferredHabitatPrimary(ecoData);
    const secondaryHabitats = getPreferredHabitatSecondary(ecoData);
    const habitatMixLabels = getHabitatMixLabels(getPreferredHabitatMix(ecoData));
    const dominantHabitatGroup = normalizeText(ecoData.habitat_group);
    const dominantWorldcoverLabel = normalizeText(ecoData.habitat_dominant_worldcover_label || ecoData.worldcover_label);
    const biomeName = normalizeText(ecoData.biomeName);
    const ecoregionName = normalizeText(ecoData.ecoregionName);
    const realm = normalizeText(ecoData.realm);
    const elevationBand = getElevationBand(ecoData.terrain_elevation_band, normalizeNumber(ecoData.terrain_elevation_m));
    const terrainSummary = getTerrainSummary(elevationBand);
    const waterContext = getWaterContext(ecoData.water_proximity_label, ecoData.water_distance_m);
    const distanceToCoastM = normalizeNumber(ecoData.distance_to_coast_m);
    const coastalContext = normalizeText(ecoData.coastal_context);
    const marineContextData = ecoData.marine_context && typeof ecoData.marine_context === 'object' && !Array.isArray(ecoData.marine_context)
        ? ecoData.marine_context
        : undefined;
    const biomeTerms = getBiomeTerms(biomeName);
    const ecoregionTerms = getEcoregionTerms(ecoregionName);
    const habitatFamily = getLandCoverFamily([
        primaryHabitat,
        dominantHabitatGroup,
        dominantWorldcoverLabel,
        ...secondaryHabitats,
        ...habitatMixLabels
    ]) as HabitatFamilyLabel;

    return {
        ecoData,
        hotspotName: hotspotName || undefined,
        hotspotNameTerms,
        latitude,
        longitude,
        coordinateTerms,
        primaryHabitat,
        secondaryHabitats,
        habitatMixLabels,
        dominantHabitatGroup,
        dominantWorldcoverLabel,
        habitatFamily,
        elevationBand,
        terrainSummary,
        waterContext,
        distanceToCoastM,
        coastalContext: coastalContext || undefined,
        isCoastal: typeof ecoData.is_coastal === 'boolean' ? ecoData.is_coastal : undefined,
        isNearCoastal: typeof ecoData.is_near_coastal === 'boolean' ? ecoData.is_near_coastal : undefined,
        coastlineConfidence: normalizeText(ecoData.coastline_confidence) || undefined,
        marineContextSource: normalizeText(marineContextData?.source) || undefined,
        marineContextIsOcean: typeof marineContextData?.isOcean === 'boolean' ? marineContextData.isOcean : undefined,
        marineContext: normalizeText(marineContextData?.marineContext) || undefined,
        marineContextConfidence: normalizeText(marineContextData?.confidence) || undefined,
        biomeName: biomeName || undefined,
        biomeTerms,
        ecoregionName: ecoregionName || undefined,
        ecoregionTerms,
        realm: realm || undefined,
        sourceFields: getSourceFields(ecoData)
    };
};

const selectPrimaryHabitat = (context: HabitatClassificationContext) => {
    const candidates = [
        context.primaryHabitat,
        context.dominantHabitatGroup,
        context.dominantWorldcoverLabel,
        ...context.habitatMixLabels
    ].filter(Boolean) as string[];

    if (candidates.length) {
        return toTitleCase(candidates[0]);
    }

    return getPrimaryHabitatLabel([], context.habitatFamily);
};

const selectSecondaryHabitats = (context: HabitatClassificationContext, primaryHabitat: string) => {
    const candidates = [
        ...context.secondaryHabitats,
        ...context.habitatMixLabels,
        context.dominantWorldcoverLabel,
        context.biomeName,
        context.ecoregionName
    ]
        .map(value => normalizeText(value))
        .filter(Boolean);

    const normalizedPrimary = normalizeText(primaryHabitat).toLowerCase();

    return uniqueStrings(candidates)
        .map(value => toTitleCase(value))
        .filter(value => value.toLowerCase() !== normalizedPrimary)
        .slice(0, 3);
};

const getSourceBasis = (context: HabitatClassificationContext): HabitatSourceBasis => ({
    worldcoverPrimary: context.dominantWorldcoverLabel,
    worldcoverSecondary: context.secondaryHabitats.length ? [...context.secondaryHabitats] : undefined,
    worldcoverMix: (() => {
        const preferredMix = getPreferredHabitatMix(context.ecoData);
        if (!preferredMix || typeof preferredMix !== 'object' || Array.isArray(preferredMix)) {
            return undefined;
        }

        const normalizedMix = Object.fromEntries(
            Object.entries(preferredMix)
                .map(([key, value]) => [key, normalizeNumber(value)])
                .filter((entry): entry is [string, number] => entry[1] !== undefined)
        );

        return Object.keys(normalizedMix).length ? normalizedMix : undefined;
    })(),
    waterContext: context.waterContext,
    elevationBand: context.elevationBand,
    terrainSummary: context.terrainSummary,
    biomeName: context.biomeName,
    ecoregionName: context.ecoregionName,
    realm: context.realm,
    distanceToCoastM: context.distanceToCoastM,
    coastalContext: context.coastalContext,
    isCoastal: context.isCoastal,
    isNearCoastal: context.isNearCoastal,
    coastlineConfidence: context.coastlineConfidence,
    marineContextSource: context.marineContextSource,
    marineContextIsOcean: context.marineContextIsOcean,
    marineContext: context.marineContext,
    marineContextConfidence: context.marineContextConfidence
});

const hasModifierTerm = (values: Array<string | undefined>, terms: string[]) => {
    const haystack = values
        .map(value => normalizeSearchText(value))
        .filter(Boolean)
        .join(' | ');

    return terms.some(term => haystack.includes(normalizeSearchText(term)));
};

const getNumericEcoDataField = (context: HabitatClassificationContext, field: string) => {
    return normalizeNumber((context.ecoData as Record<string, unknown>)[field]);
};

const hasMeaningfulForestMatrix = (context: HabitatClassificationContext) => {
    const forestFraction = getNumericEcoDataField(context, 'habitat_forest_fraction');
    return (forestFraction !== undefined && forestFraction >= 0.15)
        || hasModifierTerm(
            [
                context.primaryHabitat,
                context.dominantHabitatGroup,
                context.dominantWorldcoverLabel,
                ...context.secondaryHabitats,
                ...context.habitatMixLabels
            ],
            ['forest', 'woodland', 'tree']
        );
};

const hasLakeshoreWoodlandContext = (context: HabitatClassificationContext, habitat: string) => {
    const waterFraction = getNumericEcoDataField(context, 'habitat_water_fraction');
    const localTextValues = [
        context.primaryHabitat,
        habitat,
        context.dominantHabitatGroup,
        context.dominantWorldcoverLabel,
        context.waterContext,
        ...context.secondaryHabitats,
        ...context.habitatMixLabels
    ];
    const regionalTextValues = [
        context.biomeName,
        context.ecoregionName,
        ...context.biomeTerms,
        ...context.ecoregionTerms
    ];

    return hasMeaningfulForestMatrix(context)
        && (
            context.waterContext === 'On the shoreline'
            || hasModifierTerm(localTextValues, ['lake edge', 'lakeshore', 'shoreline'])
            || (
                (context.waterContext === 'At water / water edge' || context.waterContext === 'Near water')
                && hasModifierTerm(regionalTextValues, ['great lakes'])
            )
        )
        && ((waterFraction !== undefined && waterFraction > 0) || hasModifierTerm(localTextValues, ['water', 'lake', 'shore']));
};

const extractModifiers = (
    context: HabitatClassificationContext,
    habitat: string,
    primaryHabitat: string
) => {
    const modifiers = new Set<string>();
    const textValues = [
        context.primaryHabitat,
        primaryHabitat,
        habitat,
        context.dominantHabitatGroup,
        context.dominantWorldcoverLabel,
        context.waterContext,
        context.terrainSummary,
        context.biomeName,
        context.ecoregionName,
        context.realm,
        ...context.secondaryHabitats,
        ...context.habitatMixLabels,
        ...context.coordinateTerms,
        ...context.biomeTerms,
        ...context.ecoregionTerms
    ];

    if (
        context.coastalContext === 'coastal'
        || context.isCoastal
        || (context.distanceToCoastM !== undefined && context.distanceToCoastM <= 1000)
        || hasModifierTerm(textValues, ['coastal', 'marine', 'sea', 'ocean', 'nearshore', 'offshore'])
    ) {
        modifiers.add('coastal');
    }

    if (
        hasModifierTerm(textValues, ['shore', 'shoreline', 'beach', 'strand', 'intertidal', 'bay mouth'])
        || context.waterContext === 'On the shoreline'
    ) {
        modifiers.add('shoreline');
    }

    if (
        context.waterContext === 'On the shoreline'
        || context.waterContext === 'Near water'
        || hasModifierTerm(textValues, ['water edge', 'water-edge', 'water edge habitat'])
    ) {
        modifiers.add('water-edge');
    }

    if (hasLakeshoreWoodlandContext(context, habitat)) {
        modifiers.add('lakeshore');
    }

    if (hasModifierTerm(textValues, ['riparian', 'riverine', 'streamside', 'creekside'])) {
        modifiers.add('riparian');
    }

    if (hasModifierTerm(textValues, ['riverine'])) {
        modifiers.add('riverine');
    }

    if (context.elevationBand === 'lowland') modifiers.add('lowland');
    if (context.elevationBand === 'foothills') modifiers.add('foothill');
    if (context.elevationBand === 'montane') modifiers.add('montane');
    if (context.elevationBand === 'subalpine') modifiers.add('subalpine');
    if (context.elevationBand === 'alpine') modifiers.add('alpine');

    if (hasModifierTerm(textValues, ['conifer', 'needleleaf', 'spruce', 'fir', 'pine', 'juniper'])) {
        modifiers.add('coniferous');
    }

    if (hasModifierTerm(textValues, ['broadleaf', 'broad leaf', 'deciduous'])) {
        modifiers.add('broadleaf');
    }

    if (hasModifierTerm(textValues, ['mixed forest', 'mixed woodland'])) {
        modifiers.add('mixed-forest');
    }

    if (hasModifierTerm(textValues, ['open woodland', 'wooded grassland', 'savanna woodland', 'parkland'])) {
        modifiers.add('open-woodland');
    }

    if (hasModifierTerm(textValues, ['rock', 'rocky', 'reef', 'cliff', 'bedrock', 'talus', 'scree'])) {
        modifiers.add('rocky');
    }

    if (hasModifierTerm(textValues, ['sand', 'sandy', 'beach', 'dune', 'strand'])) {
        modifiers.add('sandy');
    }

    if (hasModifierTerm(textValues, ['salt', 'saline'])) {
        modifiers.add('saline');
    }

    if (hasModifierTerm(textValues, ['brackish', 'estuary', 'lagoon'])) {
        modifiers.add('brackish');
    }

    if (hasModifierTerm(textValues, ['freshwater', 'lake', 'river', 'stream', 'creek', 'pond', 'marsh'])) {
        modifiers.add('freshwater');
    }

    if (hasModifierTerm(textValues, ['seasonal', 'seasonally wet', 'ephemeral', 'temporary'])) {
        modifiers.add('seasonal');
    }

    if (hasModifierTerm(textValues, ['permanent water', 'perennial', 'year-round'])) {
        modifiers.add('permanent');
    }

    if (hasModifierTerm(textValues, ['urban', 'built-up', 'built up', 'settlement', 'city', 'park'])) {
        modifiers.add('urban');
    }

    if (hasModifierTerm(textValues, ['agricultural', 'cropland', 'farm', 'field', 'pasture', 'plantation'])) {
        modifiers.add('agricultural');
    }

    return Array.from(modifiers);
};

const getDerivedDisplayHabitat = (
    context: HabitatClassificationContext,
    family: HabitatFamilyLabel,
    habitat: string,
    modifiers: string[]
) => {
    const modifierSet = new Set(modifiers);
    const textValues = [
        context.primaryHabitat,
        habitat,
        context.dominantHabitatGroup,
        context.dominantWorldcoverLabel,
        context.waterContext,
        context.biomeName,
        context.ecoregionName,
        ...context.secondaryHabitats,
        ...context.habitatMixLabels,
        ...context.biomeTerms,
        ...context.ecoregionTerms
    ];

    if ((family === 'Forest' || family === 'Water' || family === 'Mixed') && hasLakeshoreWoodlandContext(context, habitat)) {
        return 'Lakeshore Woodland';
    }

    if (family === 'Forest' && (modifierSet.has('riparian') || modifierSet.has('riverine'))) {
        return 'Riverine Woodland';
    }

    if (family === 'Forest' && modifierSet.has('subalpine') && modifierSet.has('coniferous')) {
        return 'Subalpine Conifer Forest';
    }

    if (family === 'Forest' && modifierSet.has('montane')) {
        return 'Montane Forest';
    }

    if (
        family === 'Urban'
        && (
            modifierSet.has('broadleaf')
            || modifierSet.has('open-woodland')
            || modifierSet.has('water-edge')
            || hasModifierTerm(textValues, ['green', 'park', 'tree', 'woodland', 'grassland'])
        )
    ) {
        return 'Urban Green Space';
    }

    if (
        family === 'Agricultural'
        && hasModifierTerm(textValues, ['edge', 'mosaic', 'wetland', 'woodland', 'grassland'])
    ) {
        return 'Agricultural Edge';
    }

    return undefined;
};

export function buildBirdingHabitatContext(ecoData?: LocationEcoregionData, options?: HabitatClassificationBuildOptions): HabitatClassificationContext | undefined {
    if (!ecoData) return undefined;
    return buildContext(ecoData, options);
}

export function classifyBirdingHabitat(ecoData?: LocationEcoregionData, options?: HabitatClassificationBuildOptions): BirdingHabitatProfile | undefined {
    const context = buildBirdingHabitatContext(ecoData, options);
    if (!context) return undefined;

    const matchedRule = getMatchedHabitatRule(context);
    const canonicalRule = getMatchedCanonicalHabitatRule(context);
    const derivedSubtypeRule = getMatchedDerivedSubtypeHabitatRule(context);
    const primaryHabitat = matchedRule?.label || selectPrimaryHabitat(context);
    const habitat = canonicalRule?.label || primaryHabitat;
    const habitatFamily = canonicalRule?.family || matchedRule?.family || context.habitatFamily;
    const subtype = derivedSubtypeRule?.label;
    const modifiers = extractModifiers(context, habitat, primaryHabitat);
    const derivedDisplayHabitat = getDerivedDisplayHabitat(context, habitatFamily, habitat, modifiers);
    const displayHabitat = subtype || derivedDisplayHabitat || primaryHabitat;
    const secondaryHabitats = selectSecondaryHabitats(context, displayHabitat);
    const confidence = getProfileConfidence({
        primaryHabitat,
        secondaryHabitats,
        terrainSummary: context.terrainSummary,
        waterContext: context.waterContext,
        coordinateTerms: context.coordinateTerms,
        biomeTerms: context.biomeTerms,
        ecoregionTerms: context.ecoregionTerms
    });
    const explanation = getBirdingHabitatMatchExplanation(context);
    const sourceBasis = getSourceBasis(context);
    explanation.debug.extractedModifiers = modifiers;
    explanation.debug.derivedDisplayHabitat = derivedDisplayHabitat;

    return {
        version: BIRDING_HABITAT_PROFILE_VERSION,
        source: 'classifier',
        family: habitatFamily,
        habitat,
        habitatCode: undefined,
        subtype,
        displayHabitat,
        modifiers,
        sourceBasis,
        primaryHabitat,
        primaryHabitatSlug: slugifyHabitatLabel(primaryHabitat),
        habitatFamily,
        secondaryHabitats,
        terrainSummary: context.terrainSummary,
        waterContext: context.waterContext,
        confidence: matchedRule?.confidence || confidence,
        reasons: matchedRule?.reasons || makeReasonList({
            primaryHabitat,
            secondaryHabitats,
            terrainSummary: context.terrainSummary,
            waterContext: context.waterContext,
            hotspotNameTerms: context.hotspotNameTerms,
            coordinateTerms: context.coordinateTerms,
            biomeTerms: context.biomeTerms,
            ecoregionTerms: context.ecoregionTerms
        }),
        sourceFields: context.sourceFields,
        explanation
    };
}

export type {
    BirdingHabitatProfile,
    HabitatClassificationContext,
    HabitatClassificationRule,
    HabitatClassificationConfidence
} from "./types";
export {
    BIRDING_HABITAT_PROFILE_VERSION,
    HABITAT_FAMILY_LABELS,
    TERRAIN_ELEVATION_BAND_LABELS,
    TERRAIN_ELEVATION_BANDS
} from "./constants";
export {
    getBiomeTerms,
    getCoordinateTerms,
    getEcoregionTerms,
    getElevationBand,
    getLandCoverFamily,
    getPreferredHabitatMix,
    getPreferredHabitatPrimary,
    getPreferredHabitatSecondary,
    getPrimaryHabitatLabel,
    getProfileConfidence,
    getTerrainSummary,
    getWaterContext,
    makeReasonList,
    normalizeHabitatMixLabel,
    normalizeNumber,
    normalizeSearchText,
    normalizeText,
    splitTerms,
    slugifyHabitatLabel,
    toTitleCase,
    uniqueStrings
} from "./helpers";
export {
    BIRDING_HABITAT_RULES,
    CANONICAL_HABITAT_RULES,
    DERIVED_HABITAT_RULES,
    getBirdingHabitatMatchExplanation,
    getMatchedCanonicalHabitatRule,
    getMatchedDerivedDisplayHabitatRule,
    getMatchedDerivedSubtypeHabitatRule,
    getRankedCanonicalHabitatRules,
    getRankedDerivedDisplayHabitatRules,
    getRankedDerivedHabitatRules
} from "./rules";
